import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Calendar, AlertTriangle, CheckCircle, Clock, Bell } from "lucide-react";
import { motion } from "framer-motion";

const formatCurrency = (amount) => {
  if (!amount) return "-";
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

const typeLabels = {
  sgk: "SGK",
  bagkur: "Bağkur",
  kdv: "KDV",
  muhtasar: "Muhtasar",
  gecici_vergi: "Geçici Vergi",
  gelir_vergisi: "Gelir Vergisi",
  kurumlar_vergisi: "Kurumlar Vergisi",
  damga: "Damga Vergisi",
  diger: "Diğer"
};

const typeColors = {
  sgk: "bg-sky-400/10 text-sky-400",
  bagkur: "bg-emerald-400/10 text-emerald-400",
  kdv: "bg-amber-400/10 text-amber-400",
  muhtasar: "bg-purple-400/10 text-purple-400",
  gecici_vergi: "bg-rose-400/10 text-rose-400",
  gelir_vergisi: "bg-orange-400/10 text-orange-400",
  kurumlar_vergisi: "bg-teal-400/10 text-teal-400",
  damga: "bg-pink-400/10 text-pink-400",
  diger: "bg-zinc-400/10 text-zinc-400"
};

const FiscalCalendar = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/fiscal-calendar`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fiscal-calendar-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Mali Takvim</h1>
        <p className="text-zinc-500 text-sm mt-1">Vergi, SGK, Bağkur ve diğer yasal ödeme tarihleri</p>
      </div>

      {/* Urgent Alerts */}
      {data?.upcoming_deadlines?.filter(d => d.urgent).length > 0 && (
        <div className="bg-rose-400/10 border border-rose-400/20 p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-rose-400" />
            <h3 className="text-rose-400 font-medium">Acil Ödemeler</h3>
          </div>
          <div className="space-y-2">
            {data.upcoming_deadlines.filter(d => d.urgent).map((deadline, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded-sm text-xs ${typeColors[deadline.type] || typeColors.diger}`}>
                    {typeLabels[deadline.type] || deadline.type}
                  </span>
                  <span className="text-zinc-50">{deadline.name}</span>
                </div>
                <div className="text-right">
                  <p className="text-rose-400 font-mono text-sm">{deadline.date}</p>
                  <p className="text-xs text-rose-400">{deadline.days_until} gün kaldı</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calendar Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Deadlines */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="p-4 border-b border-zinc-800">
            <h3 className="text-lg font-outfit font-medium text-zinc-50 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-400" />
              Yaklaşan Son Tarihler
            </h3>
          </div>
          <div className="p-4 space-y-3">
            {data?.upcoming_deadlines?.map((deadline, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`flex items-center justify-between p-3 rounded-sm border ${
                  deadline.urgent ? 'bg-rose-400/5 border-rose-400/30' : 'bg-zinc-950 border-zinc-800'
                }`}
                data-testid={`deadline-${index}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${deadline.urgent ? 'bg-rose-400' : 'bg-amber-400'}`} />
                  <div>
                    <p className="text-sm text-zinc-50">{deadline.name}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${typeColors[deadline.type] || typeColors.diger}`}>
                      {typeLabels[deadline.type] || deadline.type}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-mono text-zinc-400">{deadline.date}</p>
                  <p className={`text-xs ${deadline.urgent ? 'text-rose-400' : 'text-zinc-500'}`}>
                    {deadline.days_until === 0 ? 'Bugün!' : `${deadline.days_until} gün`}
                  </p>
                </div>
              </motion.div>
            ))}
            {(!data?.upcoming_deadlines || data.upcoming_deadlines.length === 0) && (
              <div className="text-center py-8 text-zinc-500">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                <p>Yaklaşan son tarih yok</p>
              </div>
            )}
          </div>
        </div>

        {/* Monthly Obligations */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="p-4 border-b border-zinc-800">
            <h3 className="text-lg font-outfit font-medium text-zinc-50 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Aylık Yükümlülükler
            </h3>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-50">SGK Primi</span>
                  <span className="text-zinc-400 font-mono text-sm">Her ayın 26'sı</span>
                </div>
              </div>
              <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-50">Bağkur Primi</span>
                  <span className="text-zinc-400 font-mono text-sm">Her ayın 26'sı</span>
                </div>
              </div>
              <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-50">Muhtasar Beyanname</span>
                  <span className="text-zinc-400 font-mono text-sm">Her ayın 26'sı</span>
                </div>
              </div>
              <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-50">KDV Beyannamesi</span>
                  <span className="text-zinc-400 font-mono text-sm">Her ayın 26'sı</span>
                </div>
              </div>
            </div>

            <h4 className="text-sm font-medium text-zinc-400 mt-6 mb-3">Üç Aylık</h4>
            <div className="space-y-3">
              <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-50">Geçici Vergi Beyannamesi</span>
                  <span className="text-zinc-400 font-mono text-sm">Şubat, Mayıs, Ağustos, Kasım 17</span>
                </div>
              </div>
            </div>

            <h4 className="text-sm font-medium text-zinc-400 mt-6 mb-3">Yıllık</h4>
            <div className="space-y-3">
              <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-50">Gelir Vergisi Beyannamesi</span>
                  <span className="text-zinc-400 font-mono text-sm">31 Mart</span>
                </div>
              </div>
              <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-50">Kurumlar Vergisi Beyannamesi</span>
                  <span className="text-zinc-400 font-mono text-sm">30 Nisan</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiscalCalendar;
